import type { School, ScorecardSlice } from "../lib/types";
import { int, orDash, pct, usd } from "../lib/format";
import { StudentLoveHearts } from "./badges";

/** A near-complete reproduction of the school's US Dept. of Education College Scorecard
 *  profile, from the fields pulled in scripts/enrich-scorecard.mjs. Sub-sections only
 *  render when their data is present. */
export function ScorecardPanel({ school: s }: { school: School }) {
  if (!s.scorecard_id) {
    return (
      <section className="card lg:col-span-2">
        <PanelHeading />
        <p className="mt-2 text-sm text-slate-400">Not yet matched to a College Scorecard record.</p>
      </section>
    );
  }

  return (
    <section className="card lg:col-span-2">
      <PanelHeading url={s.scorecard_url} />
      <p className="mt-1 text-xs text-slate-500">
        {[s.ownership_type, s.locale, s.size_category].filter(Boolean).join(" · ") || "—"}
      </p>

      <div className="mt-3 grid gap-x-8 gap-y-5 md:grid-cols-2">
        <Group title="Cost">
          <Stat label="Average annual cost" hint="net price after aid, all students">
            {usd(s.net_price)}
          </Stat>
          {s.net_price_by_income && (
            <div className="mt-1">
              <div className="label mb-1">Average cost by family income</div>
              <SliceBars slices={s.net_price_by_income} format={usd} plain />
            </div>
          )}
          <TwoUp>
            <Stat label="In-state tuition">{usd(s.tuition_in_state)}</Stat>
            <Stat label="Out-of-state tuition">{usd(s.tuition_out_state)}</Stat>
          </TwoUp>
          <Stat label="Sticker cost of attendance">{usd(s.cost_attendance)}</Stat>
        </Group>

        <Group title="Graduation & retention">
          <RateBar label="Graduation rate (within 6 years)" value={s.grad_rate_4yr ?? s.grad_rate} />
          <RateBar
            label="Students who return after their first year"
            value={s.retention_rate}
          />
          {s.student_love != null && (
            <div className="flex items-center justify-between text-sm">
              <span className="label">Student Love (see card above)</span>
              <StudentLoveHearts school={s} />
            </div>
          )}
          {s.grad_rate != null && s.grad_rate_4yr != null && (
            <Stat label="Graduation rate (all students, pooled)">{pct(s.grad_rate)}</Stat>
          )}
        </Group>

        <Group title="Earnings & outcomes">
          <Stat label="Median earnings 10 years after entry">{usd(s.earnings_10yr)}</Stat>
          <RateBar
            label="Earning more than a typical high-school graduate"
            value={s.earnings_above_hs_grad}
          />
          <Stat label="Typical family income of students">{usd(s.median_family_income)}</Stat>
        </Group>

        <Group title="Financial aid & debt">
          <TwoUp>
            <Stat label="Median debt at graduation">{usd(s.median_debt)}</Stat>
            <Stat label="Typical monthly payment">
              {s.monthly_loan_payment == null ? "—" : usd(Math.round(s.monthly_loan_payment))}
            </Stat>
          </TwoUp>
          <TwoUp>
            <Stat label="Take federal loans">{pct(s.federal_loan_rate)}</Stat>
            <Stat label="Receive Pell grants">{pct(s.pct_pell)}</Stat>
          </TwoUp>
        </Group>

        <Group title="Admissions">
          <TwoUp>
            <Stat label="Acceptance rate">{pct(s.admission_rate)}</Stat>
            <Stat label="Test policy">{orDash(s.test_policy)}</Stat>
          </TwoUp>
          <TwoUp>
            <Stat label="SAT (25th–75th)">
              {s.sat_25 && s.sat_75 ? `${s.sat_25}–${s.sat_75}` : orDash(s.sat_avg && `avg ${s.sat_avg}`)}
            </Stat>
            <Stat label="ACT (25th–75th)">
              {s.act_25 && s.act_75 ? `${s.act_25}–${s.act_75}` : orDash(s.act_median && `mid ${s.act_median}`)}
            </Stat>
          </TwoUp>
          <Stat label="Average GPA">{orDash(s.avg_gpa)}</Stat>
        </Group>

        <Group title="Students">
          <TwoUp>
            <Stat label="Undergraduates">{int(s.undergrad_size)}</Stat>
            <Stat label="Graduate students">{int(s.grad_students)}</Stat>
          </TwoUp>
          <TwoUp>
            <Stat label="Women">{pct(s.pct_women)}</Stat>
            <Stat label="Part-time">{pct(s.part_time_share)}</Stat>
          </TwoUp>
          <TwoUp>
            <Stat label="Age 25 or older">{pct(s.share_25_older)}</Stat>
            <Stat label="First-generation">{pct(s.first_gen_share)}</Stat>
          </TwoUp>
        </Group>

        {s.race_mix && (
          <Group title="Race & ethnicity">
            <SliceBars slices={s.race_mix} format={(v) => pct(v, 0)} />
          </Group>
        )}

        {s.fields_of_study && (
          <Group title="Fields of study (share of degrees)">
            <SliceBars slices={s.fields_of_study} format={(v) => pct(v, 0)} />
          </Group>
        )}
      </div>

      {s.undergrad_majors && s.undergrad_majors.length > 0 && (
        <details className="mt-4 border-t border-slate-100 pt-3">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">
            All undergraduate majors ({s.undergrad_majors.length})
          </summary>
          <ul className="mt-2 columns-2 gap-6 text-sm sm:columns-3 lg:columns-4">
            {s.undergrad_majors.map((m) => (
              <li key={m} className="break-inside-avoid text-slate-700">
                {m}
              </li>
            ))}
          </ul>
        </details>
      )}

      <p className="mt-4 text-xs text-slate-400">
        Source: US Dept. of Education College Scorecard (latest available year).
        {s.avg_gpa == null && " Average GPA isn't in the federal dataset — add it per school via Edit."}
      </p>
    </section>
  );
}

function PanelHeading({ url }: { url?: string | null }) {
  return (
    <h3 className="font-semibold">
      College Scorecard
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="ml-2 text-xs font-normal">
          full profile ↗
        </a>
      )}
    </h3>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className="border-b border-slate-100 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h4>
      {children}
    </div>
  );
}

function Stat({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-sm">
      <div className="label">
        {label}
        {hint && <span className="ml-1 font-normal normal-case text-slate-400">({hint})</span>}
      </div>
      <div className="font-medium text-slate-800">{children}</div>
    </div>
  );
}

function TwoUp({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-4 gap-y-2">{children}</div>;
}

function RateBar({ label, value }: { label: string; value: number | null }) {
  const p = value == null ? null : Math.round(value * 100);
  return (
    <div className="text-sm">
      <div className="flex items-baseline justify-between">
        <span className="label">{label}</span>
        <span className="font-semibold text-slate-800">{p == null ? "—" : `${p}%`}</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-emerald-500"
          style={{ width: `${p ?? 0}%` }}
        />
      </div>
    </div>
  );
}

function SliceBars({
  slices,
  format,
  plain = false,
}: {
  slices: ScorecardSlice[];
  format: (v: number) => string;
  plain?: boolean;
}) {
  const max = Math.max(...slices.map((s) => s.value), 0.0001);
  return (
    <div className="space-y-1">
      {slices.map((s) => (
        <div key={s.label} className="flex items-center gap-2 text-xs">
          <span className="w-40 shrink-0 truncate text-slate-600" title={s.label}>
            {s.label}
          </span>
          {!plain && (
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
              <span
                className="block h-full rounded-full bg-sky-400"
                style={{ width: `${Math.max(0, (s.value / max) * 100)}%` }}
              />
            </span>
          )}
          <span
            className={`shrink-0 text-right font-medium text-slate-700 ${plain ? "ml-auto" : "w-16"}`}
          >
            {format(s.value)}
          </span>
        </div>
      ))}
    </div>
  );
}
